/**
 * Vector layers from `vectors.json` (03 §6): world-width ribbons (track reference line, pit lane,
 * oval, GP centreline pieces, walls, roads, paths, other lines) with analytic AA, and filled
 * polygons (stands, buildings, wood, pitches, pools). Built once per kind on first use; styles
 * toggle and colour them through a theme. Main-camera only by default (LAYER.MAIN).
 */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Constants } from '@babylonjs/core/Engines/constants';
import { Vector4 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import type { RGB, VectorsFile } from '../api.ts';
import { GROUP, HUB_UNIFORMS, LAYER, type UniformHub } from './engine.ts';
import { appendPolygon, appendRibbon } from './geometry.ts';
import { GLSL_HUB } from './shaders/common.ts';

export type RibbonKind = 'track' | 'pit' | 'oval' | 'gp' | 'walls' | 'roads' | 'paths' | 'other';
export type FillKind = 'stands' | 'buildings' | 'wood' | 'pitches' | 'pools';
export type VectorKind = RibbonKind | FillKind;

export interface VectorStyle {
  visible?: boolean;
  /** 0..255 */
  color: RGB;
  /** 0..1, default 1 */
  alpha?: number;
  /** ribbon width in metres (world-constant) */
  widthM?: number;
  /** draw order within the vector group (higher = on top) */
  order?: number;
}
export type VectorTheme = Partial<Record<VectorKind, VectorStyle>>;

const RIBBON_KINDS: readonly RibbonKind[] = ['track', 'pit', 'oval', 'gp', 'walls', 'roads', 'paths', 'other'];

/** Neutral M1 defaults (styles override every colour they use). */
export const DEFAULT_VECTOR_THEME: VectorTheme = {
  wood: { visible: false, color: [18, 30, 22], order: 0 },
  pitches: { visible: false, color: [22, 32, 24], order: 1 },
  pools: { visible: false, color: [16, 26, 40], order: 2 },
  buildings: { visible: false, color: [34, 34, 38], order: 3 },
  stands: { visible: false, color: [48, 44, 40], order: 4 },
  roads: { visible: false, color: [30, 30, 34], widthM: 5, order: 5 },
  paths: { visible: false, color: [26, 26, 30], widthM: 2, order: 6 },
  walls: { visible: false, color: [60, 60, 66], widthM: 0.6, order: 7 },
  other: { visible: false, color: [60, 60, 66], widthM: 2, order: 8 },
  gp: { visible: false, color: [70, 70, 76], widthM: 1, order: 9 },
  oval: { visible: true, color: [21, 25, 34], widthM: 12, order: 10 },
  pit: { visible: true, color: [40, 38, 42], widthM: 10, order: 11 },
  track: { visible: true, color: [52, 54, 60], widthM: 12, order: 12 },
};

/** Ribbon AA fringe (m): ≥ 0.5 backbuffer px up to 4 m per stage px at uPxScale ≥ 0.5 (r ≤ 2u). */
const PAD_M = 4;

const VEC_VERTEX = /* glsl */ `
precision highp float;
attribute vec3 position;
attribute float aLat;
uniform mat4 world;
uniform mat4 viewProjection;
varying float vLat;
void main() {
  vLat = aLat;
  gl_Position = viewProjection * (world * vec4(position, 1.0));
}
`;

const VEC_FRAGMENT = /* glsl */ `
precision highp float;
${GLSL_HUB}
uniform vec4 uColor;
uniform float uHalfW;
varying float vLat;
void main() {
  float a = 1.0;
  if (uHalfW > 0.0) {
    float d = abs(vLat);
    float aa = max(fwidth(vLat), 1e-6);
    a = clamp((uHalfW - d) / aa + 0.5, 0.0, 1.0);
  }
  if (a <= 0.0) discard;
  gl_FragColor = vec4(uColor.rgb, uColor.a * a);
}
`;

type Pts = readonly (readonly number[])[];

function polylinesOf(v: VectorsFile, kind: RibbonKind): { pts: Pts; closed: boolean }[] {
  const asList = (arr: readonly { pts: Pts }[]): { pts: Pts; closed: boolean }[] => arr.map((p) => ({ pts: p.pts, closed: false }));
  switch (kind) {
    case 'track':
      return [{ pts: v.ref_line.pts, closed: v.ref_line.closed }];
    case 'pit':
      return asList(v.pit_lane);
    case 'oval':
      return asList(v.oval);
    case 'gp':
      return asList(v.gp_centreline);
    case 'walls':
      return asList(v.walls);
    case 'roads':
      return asList(v.roads);
    case 'paths':
      return asList(v.paths);
    case 'other':
      return asList(v.other_lines);
  }
}

function polygonsOf(v: VectorsFile, kind: FillKind): Pts[] {
  return v[kind].map((p) => p.pts);
}

export interface VectorHost {
  scene: Scene;
  hub: UniformHub;
}

interface Layer {
  mesh: Mesh;
  mat: ShaderMaterial;
  color: Vector4;
  widthM: number;
}

export class VectorLayers {
  private readonly layers = new Map<VectorKind, Layer>();
  private theme: VectorTheme = {};
  layerMask: number;
  renderingGroupId: number;

  constructor(
    private readonly host: VectorHost,
    private readonly data: VectorsFile,
    opts: { layerMask?: number; renderingGroupId?: number; theme?: VectorTheme } = {},
  ) {
    this.layerMask = opts.layerMask ?? LAYER.MAIN;
    this.renderingGroupId = opts.renderingGroupId ?? GROUP.VECTORS;
    this.applyTheme(opts.theme ?? DEFAULT_VECTOR_THEME);
  }

  /** Apply a theme: builds missing visible kinds, toggles, recolours, rebuilds ribbons whose width changed. */
  applyTheme(theme: VectorTheme): void {
    this.theme = theme;
    for (const [kind, layer] of this.layers) {
      const st = theme[kind];
      if (!st || st.visible === false) layer.mesh.setEnabled(false);
    }
    for (const k of Object.keys(theme) as VectorKind[]) {
      const st = theme[k];
      if (!st || st.visible === false) continue;
      let layer = this.layers.get(k);
      const isRibbon = (RIBBON_KINDS as readonly string[]).includes(k);
      const w = isRibbon ? (st.widthM ?? 1) : 0;
      if (layer && isRibbon && layer.widthM !== w) {
        this.disposeLayer(k);
        layer = undefined;
      }
      layer ??= this.build(k, w);
      layer.color.set(st.color[0] / 255, st.color[1] / 255, st.color[2] / 255, st.alpha ?? 1);
      layer.mat.alpha = st.alpha ?? 1;
      layer.mesh.alphaIndex = st.order ?? 0;
      layer.mesh.setEnabled(true);
    }
  }

  get currentTheme(): VectorTheme {
    return this.theme;
  }

  /** The mesh of a kind, if built (styles may borrow it, e.g. to add it to an inset's mask). */
  mesh(kind: VectorKind): Mesh | null {
    return this.layers.get(kind)?.mesh ?? null;
  }

  setLayerMask(mask: number): void {
    this.layerMask = mask;
    for (const l of this.layers.values()) l.mesh.layerMask = mask;
  }

  private build(kind: VectorKind, widthM: number): Layer {
    const pos: number[] = [];
    const lat: number[] = [];
    const idx: number[] = [];
    if (widthM > 0) {
      for (const pl of polylinesOf(this.data, kind as RibbonKind)) appendRibbon(pl.pts, pl.closed, widthM / 2, PAD_M, pos, lat, idx);
    } else {
      for (const poly of polygonsOf(this.data, kind as FillKind)) appendPolygon(poly, pos, idx);
      for (let i = lat.length; i < pos.length / 3; i++) lat.push(0);
    }
    const mesh = new Mesh(`vec-${kind}`, this.host.scene);
    const vd = new VertexData();
    vd.positions = new Float32Array(pos);
    vd.indices = new Uint32Array(idx);
    vd.applyToMesh(mesh, false);
    mesh.setVerticesData('aLat', new Float32Array(lat), false, 1);
    mesh.isPickable = false;
    mesh.layerMask = this.layerMask;
    mesh.renderingGroupId = this.renderingGroupId;
    const mat = new ShaderMaterial(
      `vec-${kind}`,
      this.host.scene,
      { vertexSource: VEC_VERTEX, fragmentSource: VEC_FRAGMENT },
      {
        attributes: ['position', 'aLat'],
        uniforms: ['world', 'viewProjection', 'uColor', 'uHalfW', ...HUB_UNIFORMS],
        needAlphaBlending: true,
      },
    );
    mat.backFaceCulling = false;
    mat.alphaMode = Constants.ALPHA_COMBINE;
    mat.disableDepthWrite = true;
    this.host.hub.register(mat);
    const color = new Vector4(1, 1, 1, 1);
    mat.setVector4('uColor', color);
    mat.setFloat('uHalfW', widthM > 0 ? widthM / 2 : 0);
    mesh.material = mat;
    const layer: Layer = { mesh, mat, color, widthM };
    this.layers.set(kind, layer);
    return layer;
  }

  private disposeLayer(kind: VectorKind): void {
    const l = this.layers.get(kind);
    if (!l) return;
    l.mesh.dispose();
    l.mat.dispose();
    this.layers.delete(kind);
  }

  dispose(): void {
    for (const k of [...this.layers.keys()]) this.disposeLayer(k);
  }
}
